/**
 * Does this user own the circle they are currently viewing?
 *
 * Extracted to its own alias-free module so `authStore.js` and the test import
 * the SAME function. The test used to duplicate it, and that was worthless:
 * replacing the store's getter with `() => true` - granting every member
 * billing access - left the whole suite green.
 *
 * `.mjs` on purpose. This package has no `"type": "module"`, so a `.js` file is
 * CommonJS to node and the test could not import it; webpack resolves `.mjs`
 * without complaint.
 *
 * Prefers the SERVER's flag. `is_circle_owner` arrived with api #1329, and the
 * local derivation needs a user id that the profile payload does not carry - the
 * Flutter client has to dig it out of a cached login response - so the flag is
 * the only answer that is reliable on its own. The derivation stays as a
 * fallback for a session hydrated before that field existed.
 *
 * The fallback coerces rather than comparing strictly. That is defensive, not a
 * fix: both `id` and `owner_id` reach the client as native integers today, and
 * an earlier version of this comment claimed the strict compare was hiding
 * controls from real owners, which could not be substantiated.
 */
export function isCircleOwner(user) {
    if (!user) return false;
    if (typeof user.is_circle_owner === "boolean") return user.is_circle_owner;

    const company = user.company;
    if (!company) return false;

    // Both sides must be a real identifier. Coercion is what makes this
    // necessary: Number(null), Number(undefined ? 0 : '') and Number('') all
    // collapse toward 0, so a circle with no owner recorded would otherwise
    // match a user with no id and hand out ownership. The test for this caught
    // the empty-string case in the first version of this function.
    const identifier = (value) => {
        if (value === null || value === undefined || value === '') return null;
        const n = Number(value);

        return Number.isFinite(n) ? n : null;
    };

    const owner = identifier(company.owner_id);
    const me = identifier(user.id);

    return owner !== null && me !== null && owner === me;
}
